<template>
  <page-content
    title="Konsultasi Psikologi: Jadwal Konsultasi"
    :with-margin="false"
  >
    <empty-state
      v-if="schedules.length == 0"
      icon="event_note"
      icon-type="rounded"
    />
    <br />
    <div v-for="(schedule, index) in schedules" :key="schedule.id">
      <div class="d-flex align-items-center px-3 py-2">
        <span class="flex-grow-1">{{ getDayName(schedule.day) }}</span>
        <span class="badge">
          {{ getScheduleTime(schedule) }}
        </span>
        <span
          v-if="can('update-mental-health-schedule')"
          class="ml-1 p-1 pointer"
          @click="editSchedule(schedule)"
          v-tooltip="'Ubah'"
        >
          <material-icon name="edit_note" size="14" />
        </span>
        <span
          v-if="can('delete-mental-health-schedule')"
          class="p-1 text-danger pointer"
          @click="deleteSchedule(schedule)"
          v-tooltip="'Hapus'"
        >
          <material-icon name="delete" size="14" />
        </span>
      </div>
      <div v-if="index != schedules.length - 1" class="divider"></div>
    </div>
    <!-- FAB -->
    <button
      v-if="can('create-mental-health-schedule')"
      class="btn btn-success btn-rounded btn-fab d-flex align-items-center"
      @click="addSchedule()"
    >
      <material-icon name="add" size="20" class="mr-1" />
      <span class="mr-1">Tambah Jadwal</span>
    </button>
    <!-- Create schedule dialog -->
    <sweet-modal
      ref="scheduleForm"
      :title="formTitle"
      overlay-theme="dark"
      blocking
    >
      <div class="form-group input-self-error">
        <label>Hari</label>
        <v-select
          placeholder="Pilih..."
          :options="dayList"
          v-model="form.day"
          :class="{ 'input-error': !isEmpty(errors.day) }"
        />
        <p v-if="!isEmpty(errors.day)" class="self-error-message">
          {{ errors.day }}
        </p>
      </div>
      <div class="d-flex gap-2">
        <c-input
          label="Jam Mulai"
          placeholder="Misal: 08.00"
          v-model="form.timeStart"
          :error="!isEmpty(errors.timeStart)"
          :error-message="errors.timeStart"
        />
        <c-input
          label="Jam Selesai"
          placeholder="Misal: 10.00"
          v-model="form.timeEnd"
          :error="!isEmpty(errors.timeEnd)"
          :error-message="errors.timeEnd"
        />
      </div>
      <button-loading
        class="btn btn-primary btn-block"
        @click="storeSchedule()"
        :loading="saveLoading"
        :loading-color="colorPrimary"
      >
        Simpan
      </button-loading>
    </sweet-modal>
  </page-content>
</template>

<script>
import { mapGetters } from "vuex";
import { getScheduleOptionText } from "@/helpers/mental-health";
import { dayList } from "@/helpers/common";
import { SweetModal } from "sweet-modal-vue";
import { camelize } from "@ridi/object-case-converter";
import { populateFormErrors, resetFormErrors } from "@/helpers/form";

export default {
  name: "mental-health-schedule-list",
  components: {
    SweetModal,
  },
  data: function () {
    return {
      dayList,
      formBehavior: "add", // add or edit
      form: {
        id: "",
        day: "",
        timeStart: "",
        timeEnd: "",
      },
      errors: {
        day: "",
        timeStart: "",
        timeEnd: "",
      },
      saveLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      schedules: "mentalHealth/schedule/schedules",
    }),
    formTitle: function () {
      return this.formBehavior == "add" ? "Tambah Jadwal" : "Ubah Jadwal";
    },
  },
  methods: {
    getScheduleOptionText,
    getDayName: function (dayIndex) {
      return dayList[dayIndex];
    },
    getScheduleTime: function (schedule) {
      return `${this.formatTimeToIndo(
        schedule.time_start
      )} - ${this.formatTimeToIndo(schedule.time_end)} WIB`;
    },
    addSchedule: function () {
      // Reset form
      resetFormErrors(this.form);

      this.openScheduleFormModal("add");
    },
    editSchedule: function (schedule) {
      this.form.id = schedule.id;
      this.form.day = dayList[schedule.day];
      this.form.timeStart = this.formatTimeToIndo(schedule.time_start);
      this.form.timeEnd = this.formatTimeToIndo(schedule.time_end);

      this.openScheduleFormModal("edit");
    },
    deleteSchedule: function (schedule) {
      const scheduleInfo = getScheduleOptionText(
        schedule.day,
        schedule.time_start,
        schedule.time_end
      );

      this.$dialog
        .confirm(
          {
            title: "Konfirmasi",
            body: `Apakah yakin menghapus jadwal <b>${scheduleInfo}</b>?`,
          },
          { customClass: "delete-confirmation" }
        )
        .then(() => {
          this.$store
            .dispatch("mentalHealth/schedule/delete", schedule.id)
            .then(() => {
              this.$toasted.success("Jadwal berhasil dihapus.");
            })
            .catch(() => {
              this.$toasted.error("Terjadi kesalahan.");
            });
        })
        .catch(() => {
          // Do nothing
        });
    },
    openScheduleFormModal: function (behavior = "add") {
      this.formBehavior = behavior;
      this.$refs.scheduleForm.open();
    },
    closeScheduleFormModal: function () {
      this.$refs.scheduleForm.close();
    },
    storeSchedule: function () {
      this.saveLoading = true;
      resetFormErrors(this.errors);

      const dayIndex = dayList.findIndex((d) => d == this.form.day);

      const form = {
        ...(this.formBehavior == "edit" && { id: this.form.id }),
        day: dayIndex != -1 ? dayIndex : "",
        time_start: this.form.timeStart.replace(".", ":"),
        time_end: this.form.timeEnd.replace(".", ":"),
      };

      const action = this.formBehavior == "edit" ? "update" : "add";

      this.$store
        .dispatch("mentalHealth/schedule/" + action, form)
        .then(() => {
          this.saveLoading = false;

          // Reset form value
          resetFormErrors(this.form);

          this.$toasted.success("Jadwal berhasil disimpan.");
          this.closeScheduleFormModal();
        })
        .catch((error) => {
          this.saveLoading = false;

          const response = error.response;
          const code = response.status;

          if (code === 422) {
            populateFormErrors(this.errors, camelize(response.data.message));
            this.$toasted.error("Data belum benar.");
          } else {
            this.$toasted.error("Terjadi kesalahan.");
          }
        });
    },
  },
  created: function () {
    this.startPageLoading();
    this.$store
      .dispatch("mentalHealth/schedule/getAll")
      .then(() => {
        this.stopPageLoading();
      })
      .catch(() => {
        this.stopPageLoading();
      });
  },
};
</script>
